import { Building2, LayoutDashboard, Users, Activity, Settings, UserCog, ClipboardCheck, Smartphone, AlertTriangle, MessageSquareHeart } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTouchNavigation } from "@/hooks/useTouchNavigation";
import { cn } from "@/lib/utils";
import { AppVersionTag } from "@/components/common/AppVersionTag";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";

const menuItems = [
  { title: "Dashboard", url: "/super-admin/dashboard", icon: LayoutDashboard },
  { title: "Tenants", url: "/super-admin/tenants", icon: Building2 },
  { title: "Admins", url: "/super-admin/admins", icon: Users },
  { title: "Users", url: "/super-admin/users", icon: UserCog },
  { title: "Activity", url: "/super-admin/activity", icon: Activity },
  { title: "Health Monitor", url: "/super-admin/health", icon: Activity },
  { title: "Sessions Audit", url: "/super-admin/sessions-audit", icon: ClipboardCheck },
  { title: "Cancellations Review", url: "/super-admin/cancellations-review", icon: AlertTriangle },
  { title: "App Versions", url: "/super-admin/app-versions", icon: Smartphone },
  { title: "Welcome Messages", url: "/super-admin/welcome-messages", icon: MessageSquareHeart },
  { title: "Settings", url: "/super-admin/settings", icon: Settings },
];

export function SuperAdminSidebar() {
  const { open } = useSidebar();
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const { makePointerDownHandler, onPointerMove, makePointerUpNavigateHandler, onPointerCancel, makeKeyHandler } = useTouchNavigation();

  return (
    <Sidebar 
      className={cn(
        open ? "w-60" : "w-14",
        "lg:pt-16 bg-super-admin-sidebar-bg"
      )}
      collapsible="icon"
      variant={isMobile ? "sidebar" : "sidebar"}
    >
      <div className="p-4 border-b border-super-admin-sidebar-border hidden lg:block">
        <h2 className={`font-bold text-super-admin-sidebar-text ${open ? "text-xl" : "text-xs text-center"}`}>
          {open ? "Super Admin" : "SA"}
        </h2>
      </div>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-super-admin-sidebar-text-muted">Management</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                const isActive = location.pathname === item.url;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      type="button"
                      draggable={false}
                      data-nav-path={item.url}
                      onPointerDown={makePointerDownHandler(item.url)}
                      onPointerMove={onPointerMove}
                      onPointerUp={makePointerUpNavigateHandler(item.url)}
                      onPointerCancel={onPointerCancel}
                      onKeyDown={makeKeyHandler(item.url)}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(item.url);
                      }}
                      className={cn(
                        "flex items-center gap-3 px-3 py-3 rounded-lg transition-colors min-h-[44px]",
                        "touch-manipulation select-none",
                        isActive
                          ? "bg-super-admin-sidebar-active text-super-admin-sidebar-text"
                          : "text-super-admin-sidebar-text-muted hover:bg-super-admin-sidebar-hover"
                      )}
                      role="link"
                      aria-current={isActive ? "page" : undefined}
                      aria-label={item.title}
                    >
                      <item.icon className="h-5 w-5" />
                      {(open || isMobile) && <span>{item.title}</span>}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <div className="p-4 border-t border-super-admin-sidebar-border hidden lg:block">
        <SidebarTrigger className="w-full text-super-admin-sidebar-text-muted hover:text-super-admin-sidebar-text" />
      </div>
      <div className="px-2 py-1 border-t border-super-admin-sidebar-border flex items-center justify-center">
        <AppVersionTag />
      </div>
    </Sidebar>
  );
}
